/* eslint-disable @typescript-eslint/no-explicit-any */
import { adjustZodSchemaDefaultForUnknownFields } from '@juvare/platform-shared/features/validation';
import { XMLParser } from 'fast-xml-parser';
import { z } from 'zod';
import { ProductId } from './newBackgroundCheckUtils';

const subjectSchema = z.object({
    firstName: z.string().optional(),
    middleName: z.string().optional(),
    lastName: z.string().optional(),
    dob: z.number().optional(),
    ssn: z.string().optional(),
});
type Subject = z.infer<typeof subjectSchema>;

const dateEntitySchema = z.object({
    year: z.number().optional(),
    month: z.number().optional(),
    day: z.number().optional(),
});

const ssnValidationXmlSchema = z.object({
    ssn: z.number().optional(),
    issuestate: z.string().optional(),
    firstissuedyear: z.number().optional(),
    lastissuedyear: z.number().optional(),
    ssnvalid: z.string().optional(),
    message: z.string().optional(),
    death: z.string().optional(),
});
type SsnValidationXml = z.infer<typeof ssnValidationXmlSchema>;

const parsedSsnValidationSchema = z.object({
    ssn: z.string().optional(),
    stateIssuedBy: z.string().optional(),
    firstIssuedYear: z.number().optional(),
    lastIssuedYear: z.number().optional(),
    ssnValid: z.string().optional(),
    message: z.string().optional(),
    death: z.string().optional(),
});
export type ParsedSsnValidation = z.infer<typeof parsedSsnValidationSchema>;

const ssnSubjectXmlSchema = z.object({
    age: z.number().optional(),
    city: z.string().optional(),
    county: z.string().optional(),
    'date-first': dateEntitySchema.optional(),
    'date-last': dateEntitySchema.optional(),
    dob: z.string().optional(),
    gender: z.string().optional(),
    'name-first': z.string().optional(),
    'name-last': z.string().optional(),
    'name-middle': z.string().optional(),
    'phone_name-dual': z.string().optional(),
    phone_phone10: z.number().optional(),
    phone_pubnonpub: z.string().optional(),
    ssn: z.string().optional(),
    state: z.string().optional(),
    'street-name': z.string().optional(),
    'street-number': z.number().optional(),
    'street-pre-direction': z.string().optional(),
    'street-suffix': z.string().optional(),
    timezone: z.string().optional(),
    type: z.string().optional(),
    'unit-designation': z.string().optional(),
    'unit-number': z.string().optional(),
    verified: z.string().optional(),
    zip: z.number().optional(),
});
type SsnSubjectXml = z.infer<typeof ssnSubjectXmlSchema>;

const parsedSsnSubjectSchema = z.object({
    age: z.number().optional(),
    city: z.string().optional(),
    county: z.string().optional(),
    dateFirst: dateEntitySchema.optional(),
    dateLast: dateEntitySchema.optional(),
    dob: dateEntitySchema.optional(),
    gender: z.string().optional(),
    nameFirst: z.string().optional(),
    nameLast: z.string().optional(),
    nameMiddle: z.string().optional(),
    phoneNameDual: z.string().optional(),
    phonePhone10: z.number().optional(),
    phonePublicOrPrivate: z.string().optional(),
    ssn: z.string().optional(),
    state: z.string().optional(),
    streetName: z.string().optional(),
    streetNumber: z.number().optional(),
    streetPreDirection: z.string().optional(),
    streetSuffix: z.string().optional(),
    timezone: z.string().optional(),
    type: z.string().optional(),
    unitDesignation: z.string().optional(),
    unitNumber: z.string().optional(),
    verified: z.string().optional(),
    zip: z.number().optional(),
});
export type ParsedSsnSubject = z.infer<typeof parsedSsnSubjectSchema>;

const detailsSchema = z.object({
    lastName: z.string().optional(),
    firstName: z.string().optional(),
    suffix: z.string().optional(),
    dob: z.string().optional(),
    ssn: z.string().optional(),
    sex: z.string().optional(),
    race: z.string().optional(),
    heightFeet: z.string().optional(),
    heightInches: z.string().optional(),
    eyes: z.string().optional(),
    state: z.string().optional(),
    county: z.string().optional(),
    recordType: z.string().optional(),
    importDate: z.string().optional(),
    matchCriteria: z.string().optional(),
    personId: z.number().optional(),
});
type Details = z.infer<typeof detailsSchema>;

const parsedAddressSchema = z.object({
    addressNo: z.string().optional(),
    streetNo: z.string().optional(),
    streetName: z.string().optional(),
    aptNo: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
    importDate: z.string().optional(),
});
export type ParsedAddress = z.infer<typeof parsedAddressSchema>;

const noteSchema = z.object({
    tableId: z.string().optional(),
    noteNo: z.string().optional(),
    noteType: z.string().optional(),
    note: z.string().optional(),
    noteDate: z.string().optional(),
});
type Note = z.infer<typeof noteSchema>;

const crimeSchema = z.object({
    crimeNo: z.string().optional(),
    sequenceNo: z.string().optional(),
    crimeCounts: z.string().optional(),
    chargeDesc: z.string().optional(),
    chargeClass: z.string().optional(),
    acc: z.string().optional(),
    offenceDate: z.string().optional(),
    importDate: z.string().optional(),
});
type Crime = z.infer<typeof crimeSchema>;

const aliasSchema = z.object({
    akaCount: z.string().optional(),
    akaLastName: z.string().optional(),
    akaFirstName: z.string().optional(),
    akaDob: z.string().optional(),
    importDate: z.string().optional(),
});
type Alias = z.infer<typeof aliasSchema>;

const parsedChargeSchema = z.object({
    caseNo: z.string().optional(),
    chargeNo: z.number().optional(),
    chargeCode: z.string().optional(),
    chargeDesc: z.string().optional(),
    dispoDate: z.string().optional(),
    plea: z.string().optional(),
    disposition: z.string().optional(),
    offenseLevel: z.string().optional(),
    importDate: z.string().optional(),
});
export type ParsedCharge = z.infer<typeof parsedChargeSchema>;

const parsedCasesSchema = z.object({
    caseNo: z.string().optional(),
    caseType: z.string().optional(),
    courtId: z.string().optional(),
    arrestDate: z.string().optional(),
    bookingDate: z.string().optional(),
    fileDate: z.string().optional(),
    arrestingAgency: z.string().optional(),
    caseNotes: z.string().optional(),
    sentence: z.string().optional(),
    importDate: z.string().optional(),
    charges: parsedChargeSchema.array().optional(),
});
export type ParsedCases = z.infer<typeof parsedCasesSchema>;

const sexOffensesSchema = z.object({
    chargeNo: z.string().optional(),
    chargeDesc: z.string().optional(),
    convictionDate: z.string().optional(),
    lastRegisteredDate: z.string().optional(),
    importDate: z.string().optional(),
});
type SexOffenses = z.infer<typeof sexOffensesSchema>;

const inmateSchema = z.object({
    inmateId: z.string().optional(),
    sequenceNo: z.string().optional(),
    admissionDate: z.string().optional(),
    releaseDate: z.string().optional(),
    releaseReason: z.string().optional(),
    sentMax: z.string().optional(),
    sentMonth: z.string().optional(),
    sentYear: z.string().optional(),
    inmateStatus: z.string().optional(),
    inmateNote: z.string().optional(),
    importDate: z.string().optional(),
});
type Inmate = z.infer<typeof inmateSchema>;

const probationSchema = z.object({
    probationNo: z.string().optional(),
    circuit: z.string().optional(),
    beginDate: z.string().optional(),
    endDate: z.string().optional(),
    probateDay: z.string().optional(),
    probateMonth: z.string().optional(),
    probateYear: z.string().optional(),
    probateDesc: z.string().optional(),
    importDate: z.string().optional(),
});
type Probation = z.infer<typeof probationSchema>;

const paroleSchema = z.object({
    paroleNo: z.string().optional(),
    paroleAgency: z.string().optional(),
    beginDate: z.string().optional(),
    endDate: z.string().optional(),
    paroleDesc: z.string().optional(),
    importDate: z.string().optional(),
});
type Parole = z.infer<typeof paroleSchema>;

const parsedPersonSchema = detailsSchema.extend({
    address: parsedAddressSchema.optional(),
    note: noteSchema.optional(),
    crime: crimeSchema.optional(),
    alias: aliasSchema.optional(),
    cases: parsedCasesSchema.array().optional(),
    sexOffences: sexOffensesSchema.optional(),
    inmate: inmateSchema.optional(),
    probation: probationSchema.optional(),
    parole: paroleSchema.optional(),
});
export type ParsedPerson = z.infer<typeof parsedPersonSchema>;

export const parsedBackgroundCheckSchema = adjustZodSchemaDefaultForUnknownFields(
    z.object({
        subject: subjectSchema.optional(),
        persons: parsedPersonSchema.array().optional(),
        ssnSubjects: parsedSsnSubjectSchema.array().optional(),
        ssnValidationData: parsedSsnValidationSchema.optional(),
    }),
);

export type ParsedBackgroundCheck = z.infer<typeof parsedBackgroundCheckSchema>;

export function parseBackgroundCheck(xml: string): ParsedBackgroundCheck {
    const options = {
        // transformTagName: (tagName: string) => tagName.toLowerCase(),
        isArray: (name: string) => name === 'Charges',
    };
    const parser = new XMLParser(options);
    const result = parser.parse(xml);

    const xmlData = result?.xml?.URLSearchResponse;

    if (!xmlData) {
        throw new Error('No XML data available.');
    }

    try {
        const subject = getSubject(xmlData);
        const ssnSubjects = getSsnSubjects(xmlData);
        const criminalRecords = getCriminalRecords(xmlData);
        const persons = getCriminalPersons(criminalRecords);
        const ssnValidationData = getSsnValidationData(xmlData);

        return {
            subject,
            ...(ssnSubjects.length && { ssnSubjects: [...ssnSubjects] }),
            ...(persons.length && { persons: [...persons] }),
            ...(ssnValidationData && { ssnValidationData }),
        };
    } catch (e: any) {
        throw new Error('There was something wrong with the XML parse.');
    }
}

function getSsnValidationData(xmlData: any): ParsedSsnValidation | undefined {
    const components = xmlData.ComponentsList?.Component || [];
    const arrayOfComponents = Array.isArray(components) ? components : [...(components ? [components] : [])];
    const ssnComponent = arrayOfComponents.find((component: any) => {
        const { ProductId: productId } = component;

        return productId === ProductId.SsnVerification;
    });
    const ssnValidationRecord = ssnComponent?.ResultXML.SSN.response?.ssnvalidation as SsnValidationXml;

    if (!ssnValidationRecord) {
        return;
    }

    const {
        ssn,
        message,
        death,
        ssnvalid: ssnValid,
        firstissuedyear: firstIssuedYear,
        lastissuedyear: lastIssuedYear,
        issuestate: stateIssuedBy,
    } = ssnValidationRecord;

    return {
        ssn: getCleanedSsn(ssn),
        stateIssuedBy,
        firstIssuedYear,
        lastIssuedYear,
        ssnValid,
        message,
        death,
    };
}

function getSubject(xmlData: any): Subject {
    if (Array.isArray(xmlData.Subject)) {
        throw new Error('More than one element named Subject found.');
    }

    const {
        Subject: { DOB: dob, FirstName: firstName, MiddleName: middleName, LastName: lastName, SSN: ssn },
    } = xmlData;

    return {
        firstName,
        middleName,
        lastName,
        dob,
        ssn: getCleanedSsn(ssn),
    };
}

function getSsnSubjects(xmlData: any): ParsedSsnSubject[] {
    const components = xmlData.ComponentsList?.Component || [];
    const arrayOfComponents = Array.isArray(components) ? components : [...(components ? [components] : [])];
    const ssnComponent = arrayOfComponents.find((component: any) => {
        const { ResultXML } = component;

        return ResultXML.SSN;
    });
    const ssnRecords = ssnComponent?.ResultXML.SSN.response?.record || [];
    const arrayOfSsnRecords = Array.isArray(ssnRecords) ? ssnRecords : ([...(ssnRecords ? [ssnRecords] : [])] as SsnSubjectXml[]);

    return arrayOfSsnRecords.map((record) => {
        const {
            age,
            city,
            county,
            'date-first': dateFirst,
            'date-last': dateLast,
            dob,
            gender,
            'name-first': nameFirst,
            'name-last': nameLast,
            'name-middle': nameMiddle,
            'phone_name-dual': phoneNameDual,
            phone_phone10: phonePhone10,
            phone_pubnonpub: phonePubnonpub,
            ssn,
            state,
            'street-name': streetName,
            'street-number': streetNumber,
            'street-pre-direction': streetPreDirection,
            'street-suffix': streetSuffix,
            timezone,
            type,
            'unit-designation': unitDesignation,
            'unit-number': unitNumber,
            verified,
            zip,
        } = record || {};

        return (
            record && {
                ...(age && { age: age }),
                ...(city && { city: city }),
                ...(county && { county: county }),
                ...(dateFirst && { dateFirst: dateFirst }),
                ...(dateLast && { dateLast: dateLast }),
                ...(dob && { dob: dob }),
                ...(gender && { gender: gender }),
                ...(nameFirst && { nameFirst: nameFirst }),
                ...(nameLast && { nameLast: nameLast }),
                ...(nameMiddle && { nameMiddle: nameMiddle }),
                ...(phoneNameDual && { phoneNameDual: phoneNameDual }),
                ...(phonePhone10 && { phonePhone10: phonePhone10 }),
                ...(phonePubnonpub && { phonePubnonpub: phonePubnonpub }),
                ...(ssn && { ssn: getCleanedSsn(ssn) }),
                ...(state && { state: state }),
                ...(streetName && { streetName: streetName }),
                ...(streetNumber && { streetNumber: streetNumber }),
                ...(streetPreDirection && { streetPreDirection: streetPreDirection }),
                ...(streetSuffix && { streetSuffix: streetSuffix }),
                ...(timezone && { timezone: timezone }),
                ...(type && { type: type }),
                ...(unitDesignation && { unitDesignation: unitDesignation }),
                ...(unitNumber && { unitNumber: unitNumber }),
                ...(verified && { verified: verified }),
                ...(zip && { zip: zip }),
            }
        );
    });
}

function getCriminalRecords(xmlData: any) {
    const components = xmlData.ComponentsList?.Component || [];
    const arrayOfComponents = Array.isArray(components) ? components : [components];

    return arrayOfComponents.filter((component: any) => {
        const { ResultXML, RecordFound } = component;

        return ResultXML.Criminal && RecordFound === 'True';
    });
}

function getCriminalPersons(criminalRecords: any = []): ParsedPerson[] {
    const criminalPersons = criminalRecords.reduce((prevRecord: any[], record: any) => {
        const person = record.ResultXML.Criminal?.Person;
        if (Array.isArray(person)) return [...prevRecord, ...person];
        if (person) return [...prevRecord, person];
        return prevRecord;
    }, []);

    return getCriminalPersonsResolved(criminalPersons);
}

function getCriminalPersonsResolved(criminalPersons: any): ParsedPerson[] {
    return criminalPersons.map((person: any) => {
        const { Address, Note, Crime, Alias, Cases, SexOffenses, Inmate, Probation, Parole } = person;
        const details = getDetails(person);
        const address = getAddress(Address);
        const note = getNote(Note);
        const crime = getCrime(Crime);
        const alias = getAlias(Alias);
        const cases = getCases(Cases);
        const sexOffences = getSexOffenses(SexOffenses);
        const inmate = getInmate(Inmate);
        const probation = getProbation(Probation);
        const parole = getParole(Parole);

        return {
            ...details,
            ...(address && { address }),
            ...(note && { note }),
            ...(crime && { crime }),
            ...(alias && { alias }),
            ...(cases && { cases }),
            ...(sexOffences && { sexOffences }),
            ...(inmate && { inmate }),
            ...(probation && { probation }),
            ...(parole && { parole }),
        };
    });
}

function getDetails(person: any): Details {
    const {
        LastName: lastName,
        FirstName: firstName,
        Suffix: suffix,
        DOB: dob,
        SSN: ssn,
        Sex: sex,
        Race: race,
        HeightFeet: heightFeet,
        HeightInches: heightInches,
        Eyes: eyes,
        State: state,
        County: county,
        RecordType: recordType,
        ImportDate: importDate,
        MatchCriteria: matchCriteria,
        PersonID: personId,
    } = person || {};

    return (
        person && {
            ...(lastName && { lastName: lastName }),
            ...(firstName && { firstName: firstName }),
            ...(suffix && { suffix: suffix }),
            ...(dob && { dob: dob }),
            ...(ssn && { ssn: getCleanedSsn(ssn) }),
            ...(sex && { sex: sex }),
            ...(race && { race: race }),
            ...(heightFeet && { heightFeet: heightFeet }),
            ...(heightInches && { heightInches: heightInches }),
            ...(eyes && { eyes: eyes }),
            ...(state && { state: state }),
            ...(county && { county: county }),
            ...(recordType && { recordType: recordType }),
            ...(importDate && { importDate: importDate }),
            ...(matchCriteria && { matchCriteria: matchCriteria }),
            ...(personId && { personId: personId }),
        }
    );
}

function getAddress(addressObj: any): ParsedAddress {
    const {
        AddressNo: addressNo,
        StreetNo: streetNo,
        StreetName: streetName,
        AptNo: aptNo,
        City: city,
        State: state,
        Zip: zip,
        ImportDate: importDate,
    } = addressObj || {};

    return (
        addressObj && {
            ...(addressNo && { addressNo: addressNo }),
            ...(streetNo && { streetNo: streetNo }),
            ...(streetName && { streetName: streetName }),
            ...(aptNo && { aptNo: aptNo }),
            ...(city && { city: city }),
            ...(state && { state: state }),
            ...(zip && { zip: zip }),
            ...(importDate && { importDate: importDate }),
        }
    );
}

function getNote(noteObj: any): Note {
    const { TableID: tableId, NoteNo: noteNo, NoteType: noteType, Note: note, NoteDate: noteDate } = noteObj || {};

    return (
        noteObj && {
            ...(tableId && { tableId: tableId }),
            ...(noteNo && { noteNo: noteNo }),
            ...(noteType && { noteType: noteType }),
            ...(note && { note: note }),
            ...(noteDate && { noteDate: noteDate }),
        }
    );
}

function getCrime(crimeObj: any): Crime {
    const {
        CrimeNo: crimeNo,
        SequenceNo: sequenceNo,
        CrimeCounts: crimeCounts,
        ChargeDesc: chargeDesc,
        ChargeClass: chargeClass,
        ACC: acc,
        OffenceDate: offenceDate,
        ImportDate: importDate,
    } = crimeObj || {};

    return (
        crimeObj && {
            ...(crimeNo && { crimeNo: crimeNo }),
            ...(sequenceNo && { sequenceNo: sequenceNo }),
            ...(crimeCounts && { crimeCounts: crimeCounts }),
            ...(chargeDesc && { chargeDesc: chargeDesc }),
            ...(chargeClass && { chargeClass: chargeClass }),
            ...(acc && { acc: acc }),
            ...(offenceDate && { offenceDate: offenceDate }),
            ...(importDate && { importDate: importDate }),
        }
    );
}

function getAlias(aliasObj: any): Alias {
    const {
        AKACount: akaCount,
        AKALastName: akaLastName,
        AKAFirstName: akaFirstName,
        AKADOB: akaDob,
        ImportDate: importDate,
    } = aliasObj || {};

    return (
        aliasObj && {
            ...(akaCount && { akaCount: akaCount }),
            ...(akaLastName && { akaLastName: akaLastName }),
            ...(akaFirstName && { akaFirstName: akaFirstName }),
            ...(akaDob && { akaDob: akaDob }),
            ...(importDate && { importDate: importDate }),
        }
    );
}

function getCases(casesObj: any): ParsedCases[] {
    const casesArray = Array.isArray(casesObj) ? casesObj : [...(casesObj ? [casesObj] : [])];

    return casesArray.map((caseEntity) => {
        const {
            CaseNo: caseNo,
            CaseType: caseType,
            CourtID: courtId,
            ArrestDate: arrestDate,
            BookingDate: bookingDate,
            FileDate: fileDate,
            ArrestingAgency: arrestingAgency,
            CaseNotes: caseNotes,
            Sentence: sentence,
            ImportDate: importDate,
            Charges,
        } = caseEntity || {};

        const charges = getCharges(Charges);

        return (
            caseEntity && {
                ...(caseNo && { caseNo: caseNo }),
                ...(caseType && { caseType: caseType }),
                ...(courtId && { courtId: courtId }),
                ...(arrestDate && { arrestDate: arrestDate }),
                ...(bookingDate && { bookingDate: bookingDate }),
                ...(fileDate && { fileDate: fileDate }),
                ...(arrestingAgency && { arrestingAgency: arrestingAgency }),
                ...(caseNotes && { caseNotes: caseNotes }),
                ...(sentence && { sentence: sentence }),
                ...(importDate && { importDate: importDate }),
                ...(charges.length && { charges: [...charges] }),
            }
        );
    });
}

function getCharges(chargesObj: any = []): ParsedCharge[] {
    const chargesArray = Array.isArray(chargesObj) ? chargesObj : [...(chargesObj ? [chargesObj] : [])];

    return chargesArray.map((charge: any) => {
        const {
            CaseNo: caseNo,
            ChargeNo: chargeNo,
            ChargeCode: chargeCode,
            ChargeDesc: chargeDesc,
            DispoDate: dispoDate,
            Plea: plea,
            Disposition: disposition,
            OffenseLevel: offenseLevel,
            ImportDate: importDate,
        } = charge || {};

        return (
            charge && {
                ...(caseNo && { caseNo: caseNo }),
                ...(chargeNo && { chargeNo: chargeNo }),
                ...(chargeCode && { chargeCode: chargeCode }),
                ...(chargeDesc && { chargeDesc: chargeDesc }),
                ...(dispoDate && { dispoDate: dispoDate }),
                ...(plea && { plea: plea }),
                ...(disposition && { disposition: disposition }),
                ...(offenseLevel && { offenseLevel: offenseLevel }),
                ...(importDate && { importDate: importDate }),
            }
        );
    });
}

function getSexOffenses(sexOffensesObj: any): SexOffenses {
    const {
        ChargeNo: chargeNo,
        ChargeDesc: chargeDesc,
        ConvictionDate: convictionDate,
        LastRegisteredDate: lastRegisteredDate,
        ImportDate: importDate,
    } = sexOffensesObj || {};

    return (
        sexOffensesObj && {
            ...(chargeNo && { chargeNo: chargeNo }),
            ...(chargeDesc && { chargeDesc: chargeDesc }),
            ...(convictionDate && { convictionDate: convictionDate }),
            ...(lastRegisteredDate && { lastRegisteredDate: lastRegisteredDate }),
            ...(importDate && { importDate: importDate }),
        }
    );
}

function getInmate(inmateObj: any): Inmate {
    const {
        InmateID: inmateId,
        SequenceNo: sequenceNo,
        AdmissionDate: admissionDate,
        ReleaseDate: releaseDate,
        ReleaseReason: releaseReason,
        SentMax: sentMax,
        SentMonth: sentMonth,
        SentYear: sentYear,
        InmateStatus: inmateStatus,
        InmateNote: inmateNote,
        ImportDate: importDate,
    } = inmateObj || {};

    return (
        inmateObj && {
            ...(inmateId && { inmateId: inmateId }),
            ...(sequenceNo && { sequenceNo: sequenceNo }),
            ...(admissionDate && { admissionDate: admissionDate }),
            ...(releaseDate && { releaseDate: releaseDate }),
            ...(releaseReason && { releaseReason: releaseReason }),
            ...(sentMax && { sentMax: sentMax }),
            ...(sentMonth && { sentMonth: sentMonth }),
            ...(sentYear && { sentYear: sentYear }),
            ...(inmateStatus && { inmateStatus: inmateStatus }),
            ...(inmateNote && { inmateNote: inmateNote }),
            ...(importDate && { importDate: importDate }),
        }
    );
}

function getProbation(probationObj: any): Probation {
    const {
        ProbationNo: probationNo,
        Circuit: circuit,
        BeginDate: beginDate,
        EndDate: endDate,
        ProbateDay: probateDay,
        ProbateMonth: probateMonth,
        ProbateYear: probateYear,
        ProbateDesc: probateDesc,
        ImportDate: importDate,
    } = probationObj || {};

    return (
        probationObj && {
            ...(probationNo && { probationNo: probationNo }),
            ...(circuit && { circuit: circuit }),
            ...(beginDate && { beginDate: beginDate }),
            ...(endDate && { endDate: endDate }),
            ...(probateDay && { probateDay: probateDay }),
            ...(probateMonth && { probateMonth: probateMonth }),
            ...(probateYear && { probateYear: probateYear }),
            ...(probateDesc && { probateDesc: probateDesc }),
            ...(importDate && { importDate: importDate }),
        }
    );
}

function getParole(paroleObj: any): Parole {
    const {
        ParoleNo: paroleNo,
        ParoleAgency: paroleAgency,
        BeginDate: beginDate,
        EndDate: endDate,
        ParoleDesc: paroleDesc,
        ImportDate: importDate,
    } = paroleObj || {};

    return (
        paroleObj && {
            ...(paroleNo && { paroleNo: paroleNo }),
            ...(paroleAgency && { paroleAgency: paroleAgency }),
            ...(beginDate && { beginDate: beginDate }),
            ...(endDate && { endDate: endDate }),
            ...(paroleDesc && { paroleDesc: paroleDesc }),
            ...(importDate && { importDate: importDate }),
        }
    );
}

export function getCleanedSsn(ssn?: number | string) {
    if (!ssn) {
        return 'No SSN';
    }

    const cleanedSsn = ssn
        .toString()
        .replace(/[^0-9]/g, '')
        .slice(-4);

    if (cleanedSsn.length !== 4) {
        return ssn.toString();
    }

    return `***-**-${cleanedSsn}`;
}

export function cleanResults(text: string): string {
    const data = text.replace(/(<SSN>)(.*?)(<\/SSN>)/g, (match, tag1, ssn, tag2) => {
        const cleanedSsn = getCleanedSsn(ssn);

        return `${tag1}${cleanedSsn}${tag2}`;
    });

    return data.replace(/(<ssn>)(.*?)(<\/ssn>)/g, (match, tag1, ssn, tag2) => {
        const cleanedSsn = getCleanedSsn(ssn);

        return `${tag1}${cleanedSsn}${tag2}`;
    });
}
